import React from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import FormLabel from "./FormLabel";
import FormFieldError, { fieldClass } from "./FormFieldError";
import { FieldHint } from "./createEvent/CreateEventFormUi";
import { ReadableNavButton } from "./Sticker";
import { eventMapLink, eventMapQuery } from "../lib/format";

export const VENUE_NONE = "__none__";

/**
 * Shared venue / location input.
 *
 * mode="school": pick an existing school venue, or "no venue decided yet".
 * mode="event": free-text venue name + optional map link (Create Event "Where").
 */
export default function VenueInput({
  mode = "school",
  venues = [],
  venueId = VENUE_NONE,
  onVenueIdChange,
  location = "",
  onLocationChange,
  locationMapLink = "",
  onLocationMapLinkChange,
  cityHint = "",
  locationError = "",
  locationInputId,
  mapLinkInputId,
  locationTestId = "location-input",
  mapLinkTestId = "map-link-input",
  required = false,
  className = "",
  label = "Venue",
}) {
  const isSchool = mode === "school";

  function openMapSearch() {
    const q = location.trim();
    if (!q) {
      toast.error("Add a venue before opening Maps.");
      return;
    }
    const link = eventMapLink({
      location: q,
      city: cityHint,
      location_map_link: "",
    });
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
  }

  if (isSchool) {
    const value = venueId || VENUE_NONE;
    return (
      <div className={`space-y-2 ${className}`}>
        <label className="block">
          <FormLabel className="block mb-2">{label}</FormLabel>
          <select
            className="field"
            value={value}
            onChange={(e) => onVenueIdChange?.(e.target.value)}
            data-testid="venue-select"
          >
            <option value={VENUE_NONE}>No venue decided yet — contact host</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        {venues.length === 0 && (
          <FieldHint>
            No venues yet — add one on the Venues tab so classes know where to meet.
          </FieldHint>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="block">
        <FormLabel as="label" htmlFor={locationInputId} className="block mb-2">Venue / location *</FormLabel>
        <input
          id={locationInputId}
          className={fieldClass(locationError)}
          value={location}
          onChange={(e) => onLocationChange?.(e.target.value)}
          placeholder="Sala Caracas, Prenzlauer Berg"
          required={required}
          aria-invalid={Boolean(locationError)}
          aria-describedby={locationError && locationInputId ? `${locationInputId}-error` : undefined}
          data-testid={locationTestId}
        />
        {locationError && locationInputId ? (
          <FormFieldError id={`${locationInputId}-error`}>{locationError}</FormFieldError>
        ) : null}
      </div>
      <div className="block">
        <FormLabel as="label" htmlFor={mapLinkInputId} className="block mb-2">Map link (optional)</FormLabel>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <input
            id={mapLinkInputId}
            className="field flex-1 min-w-0"
            value={locationMapLink}
            onChange={(e) => onLocationMapLinkChange?.(e.target.value)}
            placeholder="https://maps.google.com/?q=…"
            aria-describedby="venue-input-map-link-hint"
            data-testid={mapLinkTestId}
          />
          <ReadableNavButton
            type="button"
            onClick={openMapSearch}
            disabled={!location.trim()}
            data-testid="map-link-search-btn"
          >
            <MapPin className="w-4 h-4" strokeWidth={2} aria-hidden /> Maps
          </ReadableNavButton>
        </div>
        <FieldHint id="venue-input-map-link-hint" className="mt-1.5" data-testid="map-link-helper">
          {locationMapLink.trim() ? (
            "Guests open your custom map link."
          ) : location ? (
            <>
              Leave blank to open{" "}
              <span className="font-semibold text-ink">
                &quot;{eventMapQuery({ location, city: cityHint })}&quot;
              </span>{" "}
              on Google Maps.{" "}
              <a
                href={eventMapLink({ location, city: cityHint, location_map_link: "" })}
                target="_blank"
                rel="noreferrer"
                className="underline font-semibold text-ink"
                data-testid="map-link-preview"
              >
                Preview
              </a>
            </>
          ) : (
            "Leave blank to open venue and city on Google Maps."
          )}
        </FieldHint>
      </div>
    </div>
  );
}

/** Normalize UI venue select value to API venue_id (null = undecided). */
export function venueIdFromSelect(value) {
  if (!value || value === VENUE_NONE || value === "__new__") return null;
  return value;
}
